import { Box, Stack } from '@mui/material'
import { ReactNode, useState } from 'react'
import { RiskLevelOption, RiskMatrixCell } from '@omnea-app/risks/risksFrontendClient'
import { ListRisksResponse } from '../../../../../../../list/useListRisks'
import { GetRiskSettingsResponse } from '../../../../../../../settings/hooks/useGetRiskSettings'
import { RiskChipTooltipAggregation } from './components/RiskChipTooltipAggregation'
import { RiskChipTooltipScore } from './components/RiskChipTooltipScore'
import { RiskScore } from './data/RiskMethodologyCalculator'
import { RiskMethodologyCalculatorFactory } from './data/RiskMethodologyCalculatorFactory'
import { RiskChipTooltipMetadata } from './types'

type Input = {
  probabilityId: string
  impactId: string
  probabilities: RiskLevelOption[]
  impacts: RiskLevelOption[]
  matrixLevels: RiskLevelOption[]
  riskMatrixCells: RiskMatrixCell[]
  hideNameInLabel?: boolean
  riskSettings?: GetRiskSettingsResponse['data']
  risksForQuestion?: NonNullable<ListRisksResponse['data']>
  setRiskIdToEdit?: (value: string) => void
} & RiskChipTooltipMetadata

type Output = {
  riskScore: RiskScore
  tooltip: ReactNode | null
  chipLabel: string
  possibleScores: number[]
  setTooltipOpen: (value: boolean) => void
}

export const useRiskMatrixScoreChipLoaded = (props: Input): Output => {
  const {
    probabilityId,
    impactId,
    probabilities,
    impacts,
    matrixLevels,
    riskMatrixCells,
    hideNameInLabel,
    riskSettings,
    showAggregationMessage,
    risksForQuestion,
    setRiskIdToEdit,
  } = props
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const RiskScoreMethodologyCalculator = RiskMethodologyCalculatorFactory(riskSettings?.riskScoreMethodology)
  const { riskMatrixScores: possibleScores } = RiskScoreMethodologyCalculator.getPossibleScores({
    riskMatrixCells,
    impacts,
    probabilities,
  })

  const riskScore = RiskScoreMethodologyCalculator.getRiskScore({
    riskMatrixCells,
    impactId,
    impacts,
    probabilities,
    probabilityId,
  })
  if (!riskScore.label) return { tooltip: null, riskScore, chipLabel: '', possibleScores, setTooltipOpen }

  const chipLabel = RiskScoreMethodologyCalculator.getChipLabel({
    score: riskScore.score,
    label: riskScore.label,
    matrixLevels,
    hideName: hideNameInLabel,
  })

  const impact = impacts.find((item) => item.id === impactId)
  if (!impact) return { tooltip: riskScore.label, riskScore, chipLabel, possibleScores, setTooltipOpen }

  const probability = probabilities.find((item) => item.id === probabilityId)
  if (!probability) return { tooltip: riskScore.label, riskScore, chipLabel, possibleScores, setTooltipOpen }

  return {
    riskScore,
    chipLabel,
    possibleScores,
    setTooltipOpen,
    tooltip: (
      <Box
        sx={{
          p: '16px',
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: 3,
          width: '250px',
          color: 'black',
          display: tooltipOpen ? 'block' : 'none',
        }}
      >
        <Stack gap={4}>
          {showAggregationMessage ? (
            <RiskChipTooltipAggregation
              viewRisksLink={props.viewRisksLink}
              aggregationEntity={props.aggregationEntity}
              risksForQuestion={risksForQuestion}
              setRiskIdToEdit={setRiskIdToEdit}
              closeTooltip={() => setTooltipOpen(false)}
            />
          ) : (
            <RiskChipTooltipScore
              riskScoreMethodology={RiskScoreMethodologyCalculator.getMethodology()}
              impact={impact}
              probability={probability}
            />
          )}
        </Stack>
      </Box>
    ),
  }
}
